import React from "react";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import ApplicationCheckBox from "my-tax/components/application-check-box";
import useStyles from "my-tax/components/family-tax-credit-application-print/family-tax-credit-application-print.style";
import MyTaxFourChildrenDependentsInfoTableRow from "my-tax/components/four-children-dependents-info-table-row";
import MyTaxCreditApplicationPrintEmployer from "my-tax/components/tax-credit-application-print-employer/tax-credit-application-print-employer";
import { DOTTED_LINE } from "my-tax/constants";
import { ocLightTheme } from "oc/themes/oc-light-theme";

interface MyTaxTwoOrMoreChildrenTaxCreditApplicationPrintProps {
  fullName?: string;
  taxNumber?: string;
  employerName?: string;
  employerTaxNumber?: string;
  applicationDate?: string;
  isNewVersion?: boolean;
  states?: {
    isModifiedDeclaration?: boolean;
    dependents?: {
      name: string;
      taxNumber: string;
      placeOfBirth: string;
      dateOfBirth: string;
      mothersName: string;
    }[];
    isEligible?: boolean;
    isEligibleThree?: boolean;
    isEligibleTwo?: boolean;
    permanentlyEntitled?: boolean;
    notApplyFromMonth?: string;
  };
}

export const MyTaxTwoOrMoreChildrenTaxCreditApplicationPrint = React.forwardRef(
  (props: MyTaxTwoOrMoreChildrenTaxCreditApplicationPrintProps, ref) => {
    const {
      fullName = "Minta Borbála",
      taxNumber = "8124440505",
      employerName,
      employerTaxNumber,
      applicationDate,
      isNewVersion = false,
      states,
    } = props;
    let year = applicationDate?.slice(0, 4);
    const classes = useStyles();

    return (
      <ThemeProvider theme={ocLightTheme}>
        <Box ref={ref}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            fontSize="15px"
            fontFamily="EB Garamond"
            style={{
              paddingTop: "20px",
            }}
          >
            <Box
              style={{
                fontSize: "16px",
                fontFamily: "EB Garamond",
                fontWeight: "bold",
              }}
            >
              {isNewVersion
                ? "Adóelőleg-nyilatkozat a két, a három, illetve a négy vagy több gyermeket nevelő anyák kedvezményének érvényesítéséről"
                : "Adóelőleg-nyilatkozat a három gyermeket nevelő anyák és a négy vagy több gyermeket nevelő anyák kedvezményének érvényesítéséről"
              }
              <Box />
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                paddingTop="2px"
              >
                A nyilatkozat benyújtásának éve: {year}
              </Box>
            </Box>
            <Box className={classes.blackBorder}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex">
                  <Box className={classes.paragraphStart}>I.</Box>
                  <Box fontWeight="normal">
                    A nyilatkozatot adó magánszemély
                  </Box>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  Módosított nyilatkozat:
                  <ApplicationCheckBox value={states?.isModifiedDeclaration} />
                </Box>
              </Box>
              <Box height="10px" />
              <Box
                style={{
                  marginTop: "5px",
                  marginBottom: "5px",
                }}
              >
                <Box display="flex">
                  neve:
                  <Box width="200px" textAlign="center">
                    {fullName}
                  </Box>
                  <Box marginLeft="70px">adóazonosító jele:</Box>
                  <Box marginLeft="10px">
                    {taxNumber || DOTTED_LINE}
                  </Box>
                </Box>
              </Box>
              <Box height="10px" />
              <Box
                style={{
                  fontWeight: "bold",
                }}
              >
                1.
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-around"
                marginTop="-20px"
              >
                <Box className={classes.tableTitle}>gyermek neve</Box>
                <Box className={classes.tableTitle}>adóazonosító jele</Box>
                <Box className={classes.tableTitle}>születési helye, ideje</Box>
                <Box
                  className={classes.tableTitle}
                  style={{
                    marginLeft: "-20px",
                  }}
                >
                  anyja neve
                </Box>
              </Box>
              <Box height="5px" />
              {(states?.dependents ?? []).map((item) => {
                const fallbackKey = `${item.name}_${item.dateOfBirth}`;
                return (
                  <MyTaxFourChildrenDependentsInfoTableRow
                    key={`dependent_${item.taxNumber || fallbackKey}`}
                    name={item.name}
                    taxNumber={item.taxNumber}
                    birthPlace={item.placeOfBirth}
                    birthDate={new Date(item.dateOfBirth).toLocaleDateString()}
                    mothersName={item.mothersName}
                  />
                );
              })}
              <Box height="20px" />
              <Box display="flex">
                <Box className={classes.paragraphStart}>2.</Box>
                <Box fontWeight="normal" display="flex">
                  Nyilatkozom, hogy jogosult vagyok, és igénybe kívánom venni
                </Box>
              </Box>
              <Box marginLeft="30px">
                {isNewVersion && (
                  <Box fontWeight="normal" display="flex">
                    - a két gyermeket nevelő anyák kedvezményét
                    <ApplicationCheckBox value={states?.isEligibleTwo} />
                  </Box>
                )}
                <Box fontWeight="normal" display="flex">
                  - a három gyermeket nevelő anyák kedvezményét
                  <ApplicationCheckBox value={states?.isEligibleThree} />
                </Box>
                <Box fontWeight="normal" display="flex">
                  - a négy vagy több gyermeket nevelő anyák kedvezményét
                  <ApplicationCheckBox value={states?.isEligible} />
                </Box>
              </Box>
              <Box height="10px" />
              <Box display="flex">
                <Box className={classes.paragraphStart}>3.</Box>
                <Box fontWeight="normal" display="flex">
                  A négy vagy több gyermeket nevelő anyák kedvezményét a nyilatkozatom visszavonásáig kérem figyelembe venni
                  (folytatólagos nyilatkozatot teszek)
                  <ApplicationCheckBox value={states?.permanentlyEntitled} />
                </Box>
              </Box>
              <Box height="10px" />
              <Box display="flex">
                <Box className={classes.paragraphStart}>4.</Box>
                <Box fontWeight="normal" display="flex">
                  {isNewVersion
                    ? "Nyilatkozom, hogy a két gyermeket nevelő/ a három gyermeket nevelő anyák/ a négy vagy több gyermeket nevelő anyák kedvezményét"
                    : "Nyilatkozom, hogy a három gyermeket nevelő anyák/négy vagy több gyermeket nevelő anyák kedvezményét"
                  }
                  <Box
                    style={{
                      paddingLeft: "10px",
                      paddingRight: "10px",
                    }}
                  >
                    {states?.notApplyFromMonth
                      ? states?.notApplyFromMonth
                      : "...."}
                    .
                  </Box>
                  hónaptól kezdődően nem
                </Box>
              </Box>
              <Box marginLeft="20px">(erre a hónapra sem) kívánom igénybe venni.</Box>
              <Box height="20px" />
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  Kelt: {applicationDate}
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  A nyilatkozatot tevő magánszemély aláírása:
                  <Box marginLeft="10px">
                    ................................................
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box height="5px" />
            <MyTaxCreditApplicationPrintEmployer
              employerName={employerName}
              employerTaxNumber={employerTaxNumber}
            />
          </Box>
        </Box>
      </ThemeProvider>
    );
  },
);

MyTaxTwoOrMoreChildrenTaxCreditApplicationPrint.displayName =
  "TwoOrMoreChildrenTaxCreditApplicationPrint";

export default MyTaxTwoOrMoreChildrenTaxCreditApplicationPrint;
